<script>
  import { computed, onBeforeUnmount, onMounted, ref, unref, useFetch, useRoute, watch } from '@nuxtjs/composition-api'
  import { mapState } from 'vuex'
  import { usePropellerFacet } from '@/composables/usePropellerFacet'
  import { productGetters } from '@/composables/propeller/getters'
  import merge from 'lodash.merge'
  import ProductsFilters from '@/components/Ecommerce/Products/ProductsFilters'
  import ProductsFiltersSkeleton from '@/components/Ecommerce/Products/ProductsFiltersSkeleton'
  import ProductPagination from '@/components/Ecommerce/Products/ProductPagination'
  import ProductCard from '@/components/Ecommerce/Products/ProductCard.vue'
  import ProductCardSkeleton from '@/components/Ecommerce/Products/ProductCardSkeleton.vue'
  import CallToAction from '@/components/CallToAction.vue'

  import { useBanners } from '@/composables/useBanners'
  import useUiHelpers from '@/composables/useUiHelpers'
  import { useView } from '@/composables/useView'
  import { validProductFilters } from '@/constants/products'

  import { setProductsListTracking } from '~/utils/utils'
  import { useAttribute, useListAttribute } from '~/composables/useAttribute'
  import { productAttributes } from '~/constants/products.module'
  import { gtmSendEvent } from '~/utils/gtm-send-event'

  export default {
    components: {
      ProductPagination,
      ProductCardSkeleton,
      ProductsFiltersSkeleton,
      ProductCard,
      CallToAction,
      ProductsFilters
    },
    props: {
      banners: {
        type: Array,
        default: null
      },
      bannerPositions: {
        type: Array,
        default: () => ([6, 16])
      },
      defaultOptions: {
        type: Object,
        default: () => ({})
      }
    },
    setup (props, { emit }) {
      const { getFacetsFromURL, reduceFilters } = useUiHelpers()

      const $container = ref(null)
      const route = useRoute()
      const loadingFor = ref(null)
      const visibleProducts = ref([])

      const trackVisibileProducts = (products) => {
        if (products.length === 4) {
          // we track 1 row of visible products in the grid for event view_item_list

          const productList = setProductsListTracking(products.map(product => ({
            ...product,
            name: productGetters.getName(product),
            productType: useAttribute(product, productAttributes.brand),
            level: useListAttribute(product, productAttributes.educationType).value.join(', ')
          })))
          gtmSendEvent('view_item_list', {
            ecommerce: {
              currency: 'EUR',
              items: productList
            },
            item_list_name: 'Productpagina',
            item_list_id: 'productpagina'
          }, null, true)
          visibleProducts.value = []
        }
      }
      watch(visibleProducts, () => trackVisibileProducts(visibleProducts.value))

      const handleVisibilityChange = (isVisible, product) => {
        if (isVisible) {
          visibleProducts.value = [...visibleProducts.value, product]
        }
      }

      const visibilityHandlers = new WeakMap()
      const visibilityHandlerFor = (product) => {
        let handler = visibilityHandlers.get(product)
        if (!handler) {
          handler = (isVisible) => handleVisibilityChange(isVisible, product)
          visibilityHandlers.set(product, handler)
        }
        return handler
      }

      // This facet is only used to fetch all the available filters
      const {
        result: resultWithoutFiltersApplied,
        search: searchWithoutFilters,
        loading: loadingFilters
      } = usePropellerFacet()

      const {
        result,
        search,
        loading
      } = usePropellerFacet()

      // The query that's modeled
      const query = ref(merge({
        categorySlug: 'examenbundel',
        ...props.defaultOptions
      }, getFacetsFromURL(true)))

      // The query where all empty and irrelevant (i.e. not a product filter) filters are removed
      const filteredQuery = computed(() => {
        return {
          ...query.value,
          filters: Object.keys(query.value.filters).filter((attribute) => {
            return !!query.value?.filters?.[attribute]?.length && validProductFilters.includes(attribute)
          }).reduce(reduceFilters(query.value.filters), {})
        }
      })

      const parameters = computed(() => {
        const filters = {
          ...query.value.filters,
          sort: query.value.sort,
          page: query.value.page
        }

        const defaultFilters = Object.keys(props.defaultOptions?.filters || {})

        const params = Object.keys(filters).reduce((acc, key) => {
          const values = Array.isArray(filters[key]) ? filters[key] : [filters[key]]
          if (defaultFilters.includes(key)) {
            return acc
          }

          const items = values.map((value) => {
            return `${key}=${value}&`
          }).join('')

          return `${acc}${items}`
        }, '?')

        return params.substring(0, params.length - 1)
      })

      // If someone filtered
      const filtered = (filters) => {
        query.value.page = 1
        query.value.filters = filters.value
        loadingFor.value = filters.trigger
        changeUrl(filters.trigger)
      }

      // If someone paginated
      const paginated = (page) => {
        query.value.page = page
        loadingFor.value = 'PAGINATION'
        changeUrl('PAGINATION')
      }

      const changeUrl = (trigger) => {
        window.history.pushState({
          filters: query.value,
          trigger
        }, '', `${route.value.path}${parameters.value}`)
      }

      const hasProducts = computed(() => !!result.value?.data?.items && result.value.data.items.length > 0)

      const products = computed(() => {
        return hasProducts.value
          ? (result.value?.data?.items ?? [])
          : []
      })

      const hasActiveFilters = computed(() => Object.keys(filteredQuery.value.filters || {}).length > 0)

      const unfilteredSidebarPayload = {
        categorySlug: 'examenbundel',
        filters: {},
        sort: 'latest'
      }

      // Nuxt 2's out-in page transition mounts two ProductGrid instances briefly;
      // loading.value gates against firing the same POST twice. Without active
      // filters, search() and searchWithoutFilters() POST identical bodies — so
      // only fire the dedicated sidebar fetch when filters are applied.
      const cachedResultMatches = (query) =>
        JSON.stringify(result.value?.input) === JSON.stringify(query)

      const refetchAfterSharedRequestClears = () => {
        const stop = watch(loading, (isLoading) => {
          if (isLoading) return
          stop()
          if (cachedResultMatches(filteredQuery.value)) return
          search(filteredQuery.value)
        })
      }

      const sidebarFacetsNeeded = () =>
        hasActiveFilters.value &&
        !loadingFilters.value &&
        !resultWithoutFiltersApplied.value?.data

      const fetchProductsOrAwaitInFlight = () => {
        if (loading.value) {
          refetchAfterSharedRequestClears()
          return Promise.resolve()
        }
        return search(filteredQuery.value)
      }

      const fetchSidebarFacetsIfNeeded = () => {
        if (!sidebarFacetsNeeded()) return Promise.resolve()
        return searchWithoutFilters(unfilteredSidebarPayload)
      }

      const loadProducts = () =>
        Promise.all([fetchProductsOrAwaitInFlight(), fetchSidebarFacetsIfNeeded()])

      const onPopState = (event) => {
        if (!event.state?.filters) return
        query.value = event.state.filters
        loadingFor.value = event.state.trigger
      }

      onMounted(() => {
        window.addEventListener('popstate', onPopState)
        if (!cachedResultMatches(filteredQuery.value)) {
          loadProducts()
          return
        }
        if (sidebarFacetsNeeded()) {
          searchWithoutFilters(unfilteredSidebarPayload)
        }
      })

      // @vue/composition-api ignores returned cleanups from onMounted.
      onBeforeUnmount(() => {
        window.removeEventListener('popstate', onPopState)
      })

      // Only SSR the unfiltered catalog: filtered URLs aren't SEO targets and
      // hit Propeller's slow Onderwijstype path. The sidebar facet fetch is
      // interactive UI — it runs client-side via ProductsFiltersSkeleton.
      const hasFilterQuery = Object.keys(route.value.query || {}).length > 0

      useFetch(async () => {
        if (hasFilterQuery) return
        // useFetch fires on SSR and on client-side route changes (covers what
        // onSSR did under the old VSF shim); gate on loading + cached input
        // to keep this to one request per query.
        if (loading.value) return
        if (cachedResultMatches(filteredQuery.value)) return
        await search(filteredQuery.value)
      })

      const {
        isInViewport,
        scrollToElement
      } = useView()

      // propeller's useFacet writes result.value unconditionally on resolve,
      // so a slow earlier request can clobber a later one. Serialize and only
      // ever fetch the latest pending query.
      let pendingQuery = null
      let isFetching = false

      const updateProducts = async () => {
        emit('filtered', filteredQuery)
        scrollToTop()
        fetchSidebarFacetsIfNeeded()

        pendingQuery = filteredQuery.value
        if (isFetching) return
        isFetching = true
        try {
          while (pendingQuery) {
            const next = pendingQuery
            pendingQuery = null
            await search(next)
          }
        } finally {
          isFetching = false
        }
      }

      const scrollToTop = () => {
        if (isInViewport($container.value)) {
          return
        }

        scrollToElement($container.value, 200)
      }

      watch(query, updateProducts, { deep: true })

      const pagination = computed(() => result.value?.data?.pagination ?? { currentPage: 1, totalPages: 1 })
      // Snapshot of every filter from the first unfiltered response. Keeps the
      // sidebar offering every option once the user applies a filter, until
      // the dedicated unfiltered facet request resolves.
      const cachedAllFilters = ref(null)

      watch(result, (newResult) => {
        if (cachedAllFilters.value) return
        const inputFilters = newResult?.input?.filters
        const isUnfiltered = !inputFilters || !Object.keys(inputFilters).length
        if (isUnfiltered && newResult?.data?.availableFilters?.length) {
          cachedAllFilters.value = newResult.data.availableFilters
        }
      }, { immediate: true })

      const availableFilters = computed(() => {
        if (resultWithoutFiltersApplied.value?.data) {
          return resultWithoutFiltersApplied.value.data.availableFilters ?? []
        }
        if (cachedAllFilters.value) return cachedAllFilters.value
        return result.value?.data?.availableFilters ?? []
      })

      const sidebarLoading = computed(() => {
        if (resultWithoutFiltersApplied.value?.data) return loadingFilters.value
        if (cachedAllFilters.value) return false
        return loading.value || loadingFilters.value
      })

      const entries = computed(() => {
        return props.banners && query.value.page === 1
          ? unref(useBanners(products, props.banners, props.bannerPositions))
          : unref(products)
      })

      return {
        result,
        sidebarLoading,
        hasProducts,
        entries,
        products,
        pagination,
        loading,
        loadingFor,
        updateProducts,
        availableFilters,
        query,
        filtered,
        paginated,
        $container,
        handleVisibilityChange,
        visibilityHandlerFor
      }
    },
    computed: {
      ...mapState({
        config: state => state.config
      })
    }
  }
</script>

<template>
  <div
    ref="$container"
    class="mx-auto xl:w-10/12 xxl:w-10/12"
  >
    <transition
      name="fade"
      mode="out-in"
    >
      <ProductsFilters
        v-if="!sidebarLoading || availableFilters.length"
        :default-options="defaultOptions"
        :available-filters="availableFilters"
        :filters="query.filters"
        :loading="loading"
        :loading-for="loadingFor"
        @filtered="filtered"
      />
      <ProductsFiltersSkeleton
        v-else
        :default-options="defaultOptions"
      />
    </transition>
    <div class="products-list">
      <transition-group
        tag="div"
        name="fade"
        mode="out-in"
      >
        <div
          v-if="!hasProducts && !loading"
          key="no-products"
          class="container px-4 md:px-8"
        >
          <h1 class="text-3xl text-center">
            Geen resultaten
          </h1>
        </div>
        <div
          v-else
          key="products"
          class="container px-4 md:px-8"
        >
          <transition-group
            name="list-transition"
            tag="div"
            class="relative grid grid-cols-2 gap-2 px-4 -mx-4 md:grid-cols-3 lg:grid-cols-4 justify-items-stretch"
          >
            <slot></slot>
            <div
              v-for="(product, index) in products"
              :key="(product.bundles ? product.bundles[0].id : product.productId) || index"
              v-observe-visibility="visibilityHandlerFor(product)"
              class="h-full"
            >
              <call-to-action
                v-if="product.contentType === 'CallToAction'"
                :fields="product"
              />
              <template v-else-if="!defaultOptions.hasBundle">
                <ProductCard
                  v-if="isNaN(product)"
                  :product="product"
                />
                <ProductCardSkeleton v-else />
              </template>
            </div>
          </transition-group>
        </div>
      </transition-group>
      <div class="container px-4 pt-5 pb-6 md:px-8">
        <ProductPagination
          :pagination="pagination"
          :loading="loading"
          :loading-for="loadingFor"
          @paginated="paginated"
        />
      </div>
    </div>
  </div>
</template>
